import { Show, createMemo, type Component, type JSX } from 'solid-js';
import { Panel } from '~ui/panel';
import { Toolbar, ToolbarSection } from '~ui/toolbar';
import { Heading, Text } from '~ui/text';
import { Button } from '~ui/button';
import { CrossIcon } from '~ui/icons';
import { NA, formatCurrency, formatNumber, formatPercent } from '~/lib/format';
import { name } from '~data';
import { accounts, resolveAccount } from '~/stores/portfolio-store';
import { adjustedCostBase, received, rocIsEstimated, rocReceived, totalReturn, totalReturnPct } from '~/stores/acb-store';
import { realised } from '~/stores/positions';
import { closeHolding } from '~/stores/accounts-view-store';
import Transactions from '~/features/transactions';

// One position, in the panel beside the accounts.
//
// The same fund in two accounts is two positions here, not one: cost base, the
// return of capital carried against it and the buys behind it are all per
// account, so the panel is keyed on the pair.

const Fact: Component<{ label: string; children: JSX.Element; hint?: string }> = (props) => (
    <div class='flex h-row items-center justify-between gap-2 border-b px-2 whitespace-nowrap' title={props.hint}>
        <Text tone='muted'>{props.label}</Text>
        {props.children}
    </div>
);

export const HoldingPanel: Component<{ accountId: string; symbol: string }> = (props) => {
    const account = createMemo(() => accounts().find((a) => a.id === props.accountId));
    const held = createMemo(() => (account() == null ? null : (resolveAccount(account()!).find((r) => r.symbol === props.symbol) ?? null)));

    const book = () => held()?.holding.bookCost ?? 0;
    const roc = () => rocReceived(props.accountId, props.symbol);
    const gain = () => held()?.gain ?? null;
    // Sales out of this position, whether or not any of it is still held — which
    // is why it reads off the walk rather than off the resolved holding.
    const crystallised = () => realised({ accountId: props.accountId, symbol: props.symbol });
    const signed = (v: number | null): 'green' | 'red' | undefined => (v == null ? undefined : v >= 0 ? 'green' : 'red');

    return (
        <Panel>
            <Toolbar>
                <ToolbarSection class='min-w-0'>
                    <Heading level={2} class='truncate pl-2'>
                        {props.symbol}
                    </Heading>
                    <Text tone='muted' class='truncate pl-2'>
                        {account()?.label ?? ''}
                    </Text>
                </ToolbarSection>
                <ToolbarSection>
                    <Button size='fill' icon={<CrossIcon />} title='Close' onClick={closeHolding} />
                </ToolbarSection>
            </Toolbar>

            <div class='min-h-0 flex-1 overflow-y-auto scrollbar-hide'>
                <Show when={held()} fallback={<Text tone='muted' class='block px-2 py-4 text-center'>This position is no longer held</Text>}>
                    <div class='flex h-row items-center border-b px-2'>
                        <Text class='truncate'>{name(props.symbol) ?? NA}</Text>
                    </div>

                    <Fact label='Units'>{formatNumber(held()!.holding.units, { maximumFractionDigits: 4 })}</Fact>
                    <Fact label='Market value'>
                        <Text weight='medium'>{formatCurrency(held()!.value)}</Text>
                    </Fact>
                    <Fact label='Book cost' hint='What you paid, including commissions'>
                        {formatCurrency(book())}
                    </Fact>
                    {/* Both, not one. Book cost is what you paid; cost base is
                        what it counts as after return of capital, and it is the
                        one the gain is measured against on a sale. */}
                    <Fact label='Cost base' hint='Book cost less the return of capital received'>
                        {formatCurrency(adjustedCostBase(props.accountId, props.symbol, book()))}
                    </Fact>
                    <Fact label='Return of capital' hint={rocIsEstimated(props.accountId, props.symbol) ? 'Estimated from the fund’s published tax character' : undefined}>
                        <Text tone={rocIsEstimated(props.accountId, props.symbol) ? 'muted' : undefined}>{formatCurrency(roc())}</Text>
                    </Fact>
                    <Fact label='Distributions received'>
                        <Text tone='green'>{formatCurrency(received(props.accountId, props.symbol))}</Text>
                    </Fact>
                    <Fact label='Unrealised gain'>
                        <Text tone={signed(gain())}>{formatCurrency(gain())}</Text>
                    </Fact>
                    {/* Only once something has been sold. A permanent "$0
                        realised" on a position nobody has trimmed is a row that
                        never says anything. */}
                    <Show when={crystallised() !== 0}>
                        <Fact label='Realised gain' hint='What the sales fetched, less the cost base they relieved'>
                            <Text tone={signed(crystallised())}>{formatCurrency(crystallised())}</Text>
                        </Fact>
                    </Show>
                    {/* Against book cost, not cost base — counting the return of
                        capital as a gain *and* as income would be the same
                        dollars twice. */}
                    <Fact label='Total return' hint='Gain plus everything received, against what you paid'>
                        <Text tone={signed(totalReturn(props.accountId, props.symbol, book(), held()!.value))} weight='medium'>
                            {formatCurrency(totalReturn(props.accountId, props.symbol, book(), held()!.value))} · {formatPercent(totalReturnPct(props.accountId, props.symbol, book(), held()!.value), 1)}
                        </Text>
                    </Fact>
                    <Fact label='Income / yr'>
                        <Text tone='green'>{formatCurrency(held()!.income)}</Text>
                    </Fact>
                    <Fact label='Yield'>{formatPercent(held()!.yieldPct, 2)}</Fact>
                    <Fact label='Yield on cost' hint='Income against what the position actually cost'>
                        {formatPercent(held()!.yieldOnCost, 2)}
                    </Fact>
                </Show>

                {/* The trades behind it. Scoped to this account and this fund,
                    so it is the record of how exactly this position came to
                    be — and, once anything has been sold, of what it was. */}
                <div class='flex h-row items-center border-b bg-muted px-2'>
                    <Heading level={2}>Trades</Heading>
                </div>
                <Transactions accountId={props.accountId} symbol={props.symbol} />
            </div>
        </Panel>
    );
};

export default HoldingPanel;
